import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import Button from 'src/components/Button';
import Checkbox from 'src/components/Checkbox';
import TextField from 'src/components/TextField';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';

import { SignerFormData, signerSchema } from './adminSetup.schema';
import { AdminSchool } from './adminSetup.types';
import { useAddSchoolSignerMutation } from './adminSetupApi';
import { describeApiError } from './describeApiError';

type Props = { school: AdminSchool };

const SchoolSignerForm = ({ school }: Props) => {
  const dispatch = useAppDispatch();
  const [addSigner, { isLoading }] = useAddSchoolSignerMutation();
  const { control, handleSubmit, reset } = useForm<SignerFormData>({
    resolver: zodResolver(signerSchema),
    mode: 'onChange',
    defaultValues: { firstName: '', lastName: '', email: '', isMain: school.signers.length === 0 },
  });

  const submit = handleSubmit(async (data) => {
    try {
      await addSigner({ schoolId: school.id, body: data }).unwrap();
      dispatch(addToast({ text: 'Signer added', type: 'positive' }));
      reset();
    } catch (error) {
      dispatch(
        addToast({ text: describeApiError(error, 'Could not add signer'), type: 'negative' }),
      );
      console.error('Could not add signer', error);
    }
  });

  const text = (name: 'firstName' | 'lastName' | 'email', label: string) => (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField label={label} error={error?.message} {...field} />
      )}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="text-neutral-300 typography-body-sm">
        Current signers for <span className="font-bold">{school.name}</span>:{' '}
        {school.signers.length === 0
          ? 'none'
          : school.signers
              .map((s) => `${s.firstName} ${s.lastName} (${s.email})${s.isMain ? ' • main' : ''}`)
              .join(', ')}
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {text('firstName', 'First name')}
        {text('lastName', 'Last name')}
        {text('email', 'Email')}
        <div className="flex flex-col justify-end gap-2">
          <Controller
            name="isMain"
            control={control}
            render={({ field }) => (
              <Checkbox
                label="Main signer"
                isChecked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <Button type="submit" isLoading={isLoading}>
            Add signer
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SchoolSignerForm;
